import { useState, useEffect } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';
import type { Location } from './LocationsPage';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Tenant = { id: string; name: string };

type Props = {
  location: Location | null;
  onClose: () => void;
  onSaved: () => void;
};

export function LocationFormDialog({ location, onClose, onSaved }: Props) {
  useEscapeToClose(onClose);
  const isEdit = !!location;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState(location?.tenantId || '');
  const [name, setName] = useState(location?.name || '');
  const [address, setAddress] = useState(location?.address || '');
  const [city, setCity] = useState(location?.city || '');
  const [posMode, setPosMode] = useState<'store' | 'restaurant'>(location?.posMode || 'store');
  // Kolona `store_layout` ostaje u bazi zbog zatecenih zapisa; niko je vise ne
  // cita, pa se uvek salje 'modern' da stari 'retro' zapisi izadju iz upotrebe.
  const storeLayout = 'modern';
  const [restrictPayments, setRestrictPayments] = useState(location?.restrictPayments || false);
  const [stockMode, setStockMode] = useState<'strict' | 'warn' | 'lenient'>(location?.stockMode || 'strict');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ucitaj tenant-e za create mode
  useEffect(() => {
    if (isEdit) return;
    api.get<Tenant[]>('/admin/tenants').then(setTenants).catch(() => {});
  }, [isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Naziv je obavezan'); return; }
    if (!isEdit && !tenantId) { setError('Tenant je obavezan'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), address, city, posMode, storeLayout, restrictPayments, stockMode };
      if (isEdit) {
        await api.put(`/admin/locations/${location.id}`, body);
      } else {
        await api.post('/admin/locations', { ...body, tenantId });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={dialogStyle}>
        <header style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {isEdit ? `Izmena: ${location?.name}` : 'Nova lokacija'}
          </h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>

        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!isEdit && (
            <Field label="Tenant (klijent) *">
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} required style={inputStyle}>
                <option value="">- Izaberi klijenta -</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Naziv *">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus={isEdit} required style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Adresa">
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Grad">
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)} style={inputStyle} />
            </Field>
          </div>

          {/* Izbor „Modern / Retro" je uklonjen 2026-08-26 - retro ekran je ukinut.
              Prikaz artikala se sada bira PO UREDJAJU u POS-u
              (Podesavanja → POS prikaz: slike / bez slika / interne sifre),
              jer to zavisi od kase za kojom se radi, ne od lokacije. */}
          <Field label="POS režim">
            <select value={posMode} onChange={(e) => {
              const v = e.target.value as 'store' | 'restaurant';
              setPosMode(v);
              if (v === 'restaurant') setRestrictPayments(true);
            }} style={inputStyle}>
              <option value="store">Prodavnica (store)</option>
              <option value="restaurant">Restoran (restaurant)</option>
            </select>
          </Field>

          <Field label="Stock mod (politika negative stock-a)">
            <select value={stockMode} onChange={(e) => setStockMode(e.target.value as any)} style={inputStyle}>
              <option value="strict">Strict - blokira prodaju u minus (preporuka za retail)</option>
              <option value="warn">Warn - dozvoli + logiraj upozorenje</option>
              <option value="lenient">Lenient - uvek dozvoli (preporuka za restoran)</option>
            </select>
          </Field>

          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 0' }}>
              <input type="checkbox" checked={restrictPayments} onChange={(e) => setRestrictPayments(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
              <span style={{ fontSize: 13, color: 'var(--color-text)' }}>SUF P7: blokira načine plaćanja Card / Check / Mobile</span>
            </label>
          </Field>

          {error && (
            <div style={errorBox}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving || !name.trim()} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving || !name.trim() ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      {children}
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 50,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.6)',
};

const dialogStyle: React.CSSProperties = {
  width: '100%', maxWidth: 560,
  background: 'var(--color-bg-surface)',
  borderRadius: 12,
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  border: '1px solid var(--color-border)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '18px 24px', borderBottom: '1px solid var(--color-border)',
};

const closeBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: 'var(--color-text-muted)',
  cursor: 'pointer', padding: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: 'var(--color-bg)', color: 'var(--color-text-strong)',
  border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 13, outline: 'none',
};

const btnPrimary: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '9px 16px', borderRadius: 6, border: 'none',
  background: 'var(--color-primary)', color: '#fff',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 6,
  background: 'var(--color-bg)', color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', borderRadius: 6,
  background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
  fontSize: 13,
};
